import { useCallback, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { IRealChart } from '@/music/ireal/types';
import { useStore } from '@/state/store';
import {
  layoutReadingChart,
  readingFontSize,
  readingGutter,
  readingMeasureWidth,
} from './readingChartLayout';

export function readingChartVars(chart: IRealChart, width: number, scale: number): CSSProperties {
  const fontSize = readingFontSize(width, scale);
  return {
    '--reading-symbol-size': `${fontSize}px`,
    '--reading-gutter': `${readingGutter(chart, fontSize)}px`,
  } as CSSProperties;
}

/** Measure unconstrained copies of the same typography the reader sees.
 * Fonts, dialect and alternates participate; resizing never edits the score. */
export function useReadingChartMetrics(
  chart: IRealChart,
  width: number,
  scale: number,
  enabled: boolean,
) {
  const dialect = useStore((s) => s.chordSymbolStyle);
  const ref = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<{
    chart: IRealChart;
    fontSize: number;
    dialect: string;
    widths: number[];
  }>();
  const fontSize = readingFontSize(width, scale);
  const measure = useCallback(() => {
    if (!enabled || !ref.current) return;
    const widths = [...ref.current.children].map((bar, index) => {
      const chordWidths = [...bar.children].map((chord) => chord.getBoundingClientRect().width);
      return Math.ceil(readingMeasureWidth(chart, index, fontSize, chordWidths) * 4) / 4;
    });
    setMeasured((previous) =>
      previous?.chart === chart &&
      previous.fontSize === fontSize &&
      previous.dialect === dialect &&
      previous.widths.length === widths.length &&
      previous.widths.every((w, i) => w === widths[i])
        ? previous
        : { chart, fontSize, dialect, widths },
    );
  }, [enabled, chart, fontSize, dialect]);
  useLayoutEffect(() => {
    if (!enabled) return;
    measure();
    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) measure();
    });
    document.fonts?.addEventListener('loadingdone', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (ref.current) observer?.observe(ref.current);
    return () => {
      cancelled = true;
      observer?.disconnect();
      document.fonts?.removeEventListener('loadingdone', measure);
    };
  }, [enabled, measure]);
  const layout = useMemo(() => {
    if (!enabled) return { placements: [], fontSize, rowCount: 0 };
    const widths =
      measured?.chart === chart && measured.fontSize === fontSize && measured.dialect === dialect
        ? measured.widths
        : chart.measures.map((m, i) =>
            readingMeasureWidth(
              chart,
              i,
              fontSize,
              m.chords.map((c) => Math.max(1, c.symbol.length) * fontSize * 0.5),
            ),
          );
    return layoutReadingChart(chart, width, fontSize, widths);
  }, [enabled, chart, width, fontSize, measured, dialect]);
  return { ref, layout };
}
